import React from 'react';

import { Cancel, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import { Autocomplete, IconButton, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import Input from '../../../../../components/forms/Input';
import Select from '../../../../../components/forms/Select';
import { StyledLabel } from '../../../../../components/forms/styles';
import ClassesContainer from '../ClassesContainer';
import { type Option, TimeFilter } from '../types';

interface ClassFiltersInterface {
    setPage: React.Dispatch<React.SetStateAction<number>>;
}

const Container = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr) 40px;
    gap: 10px;
    margin-bottom: 0.5rem;
`;

const Column = styled.div`
    max-width: calc(1200px / 5);
`;

const StyledIconButton = styled(IconButton)`
    margin-top: 1.5rem;
`;

const Count = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
`;

const ClassFilters: React.FC<ClassFiltersInterface> = ({ setPage }) => {
    const { t } = useTranslation('admin_settings');

    const {
        instructors,
        filteredClasses,
        selectedInstructor,
        setSelectedInstructor,
        searchValue,
        setSearchValue,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        timeFilter,
        setTimeFilter
    } = useContainer(ClassesContainer);

    const clearAllFilters = () => {
        setPage(1);
        setSearchValue('');
        setTimeFilter(TimeFilter.Current);
        setSelectedInstructor(null);
        setStartDate(null);
        setEndDate(null);
    };

    const onSearchChange = (value: string) => {
        setPage(1);
        setSearchValue(value);
    };

    const onStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
    };

    const onEndDateChange = (date: Dayjs | null) => {
        setEndDate(date);
    };

    const onTimeFilterChange = (event) => {
        setPage(1);
        setTimeFilter(event.target.value);
    };

    const options: Option[] = instructors.map((instructor) => {
        const { user_name: username, email } = instructor;

        return {
            email,
            label: username
        };
    });

    const returnFilterCount = () => {
        const hasFilters = searchValue !== '' || selectedInstructor || startDate || endDate;

        if (hasFilters) {
            const returnText = (count: number): string => {
                switch (count) {
                    case 0:
                        return t('classes.no-classes-found');
                    case 1:
                        return t('classes.showing-1-results');
                    default:
                        return t('classes.showing-results-with-count', { count });
                }
            };

            return <div>{returnText(filteredClasses.length)}</div>;
        }

        return null;
    };

    return (
        <>
            <Container>
                <Column>
                    <Input
                        fullWidth
                        id="search"
                        size="small"
                        value={searchValue}
                        label={t('classes.search')}
                        onChange={(e) => onSearchChange(e.target.value)}
                        name="search"
                        endAdornment={
                            <InputAdornment position="end">
                                <Search />
                            </InputAdornment>
                        }
                    />
                </Column>

                <Column>
                    <Select
                        name="time-filter"
                        fullWidth
                        value={timeFilter}
                        label={t('classes.status')}
                        onChange={onTimeFilterChange}
                    >
                        <MenuItem value={TimeFilter.All}>{t('classes.show-all')}</MenuItem>
                        <MenuItem value={TimeFilter.Current} selected>
                            {t('classes.show-current')}
                        </MenuItem>
                        <MenuItem value={TimeFilter.Completed}>{t('classes.show-past')}</MenuItem>
                    </Select>
                </Column>

                <Column>
                    <StyledLabel htmlFor="instructor">{t('classes.instructor')}</StyledLabel>
                    <Autocomplete
                        value={selectedInstructor}
                        onChange={(_, newValue) => {
                            setPage(1);
                            setSelectedInstructor(newValue);
                        }}
                        isOptionEqualToValue={(option, val) => option.email === val.email}
                        id="controllable-states-demo"
                        options={options}
                        size="small"
                        renderInput={(params) => <TextField {...params} placeholder={t('classes.all-instructor')} />}
                        renderOption={(props, option) => (
                            <li {...props} key={option.email}>
                                {option.label}
                            </li>
                        )}
                    />
                </Column>

                <Column>
                    <StyledLabel htmlFor="from">{t('classes.from')}</StyledLabel>
                    <DatePicker
                        value={startDate}
                        onChange={(date: Dayjs | null) => onStartDateChange(date)}
                        renderInput={(params) => <TextField size="small" fullWidth id="from" name="from" {...params} />}
                    />
                </Column>

                <Column>
                    <StyledLabel htmlFor="to">{t('classes.to')}</StyledLabel>
                    <DatePicker
                        value={endDate}
                        onChange={(date: Dayjs | null) => onEndDateChange(date)}
                        renderInput={(params) => <TextField size="small" fullWidth id="to" name="to" {...params} />}
                    />
                </Column>

                <Column>
                    <Tooltip arrow title={t('classes.clear-selection')}>
                        <StyledIconButton onClick={clearAllFilters}>
                            <Cancel />
                        </StyledIconButton>
                    </Tooltip>
                </Column>
            </Container>
            <Count>{returnFilterCount()}</Count>
        </>
    );
};

export default ClassFilters;
